import { atom } from 'infrastructure/state/jotai.wrapper'
import { initialTransform } from './initialTransform'
import { NodeType } from 'shared/types/ingestion'
import { NormalizedIngestionSource, PartnerIngestionConfig } from '../types'
import { stringOrNumberSort } from '../../../components/Helpers'
import { odellAttributeIngestion } from './ingestionConfig'
import { sourceDataAtom } from '../atoms'

const odellTypes = [
  'kid:Course',
  'our:Module',
  'our:Unit',
  'our:Lesson',
  'our:Activity',
]

const odellMaterialsAtom = atom((get) => {
  const sourceData = get(sourceDataAtom)
  if (!sourceData || !odellTypes.includes(sourceData.type)) return

  const materials: Record<string, any> = {}
  const toCheck = [sourceData]
  while (toCheck.length) {
    const current = toCheck.shift()!
    toCheck.push(...(current.children ?? []))
    const material = current.material
    if (Array.isArray(material)) {
      material.forEach((m) => (materials[m.name.toLowerCase()] = m))
    }
  }

  return materials
})

export const odellIngestionConfigAtom = atom((get): PartnerIngestionConfig => {
  const materials = get(odellMaterialsAtom)
  return {
    dataForNode,
    filterSourceFiles,
    initialTransform,
    attributeIngestion: odellAttributeIngestion,
    stringContentPostProcessing: (content: string) => {
      if (!materials) return content
      return doTheStringUpdates(content, materials)
    },
  }
})

function dataForNode(
  destinationType: NodeType,
  sourceData: NormalizedIngestionSource,
): Record<string, string | number> {
  switch (destinationType) {
    case NodeType.Course: {
      return {
        gradeID: Number(sourceData.grade) + 1,
        attribution: 'ODELL',
        subjectID: Number(sourceData.subjectId),
      }
    }
    default:
      return {}
  }
}

function filterSourceFiles(files: Record<string, string>) {
  const byGrade = Object.keys(files).reduce<
    Record<string, { key: string; URL: string }[]>
  >((acc, key) => {
    if (!key.endsWith('.html') || key.includes('/')) return acc
    // pull "g9" or "g10" from the key (e.g., "g10-d3.html" -> "g10")
    const match = key.match(/^g\d+/)
    if (!match) return acc // skip if it doesn't match the pattern

    const gradePart = match[0].slice(1) // e.g., "g9" or "g10"
    if (!(gradePart in acc)) {
      acc[gradePart] = []
    }
    acc[gradePart].push({ key, URL: files[key] })
    return acc
  }, {})

  return Object.keys(byGrade)
    .sort(stringOrNumberSort)
    .map((key) => {
      return { title: `Grade ${key}`, sourceFiles: byGrade[key] }
    })
}

function doTheStringUpdates(
  value: string,
  materials: Record<string, { pdfUrl?: string }>,
) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(value, 'text/html')

  const anchors = Array.from(doc.body.querySelectorAll('a'))

  anchors.forEach((a) => {
    if (a.href) return
    const href = findTheMaterialPdfLink(a.textContent!, materials)
    if (!href) return
    a.setAttribute('href', href)
    a.setAttribute('target', '_blank')
  })

  return doc.body.innerHTML
}

function findTheMaterialPdfLink(
  name: string,
  materials: Record<string, { pdfUrl?: string }>,
) {
  const lookup = name.toLowerCase()
  if (lookup in materials) return materials[lookup].pdfUrl
  const key = Object.keys(materials).filter((k) => k.includes(lookup))
  if (key.length !== 1) return
  return materials[key[0]].pdfUrl
}
